/**
 * Module Specs Index
 *
 * This file registers all module specifications with the module registry.
 * Import this file once at application startup to initialize the registry.
 *
 * Navigation structure:
 * - Connect: Channels, Integrations, Email Domains
 * - Build: Workflows, Chatbots, Templates
 * - Run: Campaigns, Conversations, Calendar, Cart, Tickets
 * - Observe: Social Listening, Customer Platform (CDP)
 * - Team: Team, Workload Settings, Team Insights
 * - Settings: Files, Billing, Account Settings, Logs
 *
 * Note: Help Center and Support Tickets are rendered in a pinned sidebar
 * footer (see `src/components/sidebar.tsx`) rather than as group items —
 * they are external/support resources, not part of the platform admin nav.
 */

import { registerModules } from "../registry";
import type { ModuleSpec } from "../types";

// Import all module specs
import { homeSpec } from "./home.spec";
import { workflowsSpec } from "./workflows.spec";
import { chatbotsSpec } from "./chatbots.spec";
import { templatesSpec } from "./templates.spec";
import { channelsSpec } from "./channels.spec";
import { integrationsSpec } from "./integrations.spec";
import { emailDomainsSpec } from "./email-domains.spec";
import { campaignsSpec } from "./campaigns.spec";
import { conversationsSpec } from "./conversations.spec";
import { calendarSpec } from "./calendar.spec";
import { cartSpec } from "./cart.spec";
import { ticketsSpec } from "./tickets.spec";
import { analyticsSpec } from "./analytics.spec";
import { socialListeningSpec } from "./social-listening.spec";
import { logsSpec } from "./logs.spec";
import { customerPeopleSpec } from "./customer-people.spec";
import { customerSegmentsSpec } from "./customer-segments.spec";
import { customerEventsSpec } from "./customer-events.spec";
import { customerLabelsSpec } from "./customer-labels.spec";
import { customerDashboardSpec } from "./customer-dashboard.spec";
import { customerRulesSpec } from "./customer-rules.spec";
import { customerGoalsSpec } from "./customer-goals.spec";
import { customerPropertiesSpec } from "./customer-properties.spec";
import { customerPersonasSpec } from "./customer-personas.spec";
import { customerListsSpec } from "./customer-lists.spec";
import { customerAiActionsSpec } from "./customer-ai-actions.spec";
import { customerScoringSpec } from "./customer-scoring.spec";
import { usersSpec } from "./users.spec";
import { filesSpec } from "./files.spec";
import { billingSpec } from "./billing.spec";
import { accountSettingsSpec } from "./account-settings.spec";
import { contactCenterSettingsSpec } from "./contact-center-settings.spec";
import { helpCenterSpec } from "./help-center.spec";
import { supportTicketsSpec } from "./support-tickets.spec";

/**
 * All module specifications.
 * Add new modules to this array to register them.
 *
 * Grouped by nav group so the file mirrors the sidebar at a glance.
 */
export const allModuleSpecs: ModuleSpec[] = [
  // Overview
  homeSpec,

  // Connect
  channelsSpec,
  integrationsSpec,
  emailDomainsSpec,

  // Build
  workflowsSpec,
  chatbotsSpec,
  templatesSpec,

  // Run
  campaignsSpec,
  conversationsSpec,
  calendarSpec,
  cartSpec,
  ticketsSpec,

  // Observe
  socialListeningSpec,
  logsSpec,
  customerDashboardSpec,
  customerPeopleSpec,
  customerSegmentsSpec,
  customerEventsSpec,
  customerLabelsSpec,
  customerRulesSpec,
  customerGoalsSpec,
  customerPropertiesSpec,
  customerPersonasSpec,
  customerListsSpec,
  customerAiActionsSpec,
  customerScoringSpec,

  // Team
  usersSpec,
  contactCenterSettingsSpec,
  analyticsSpec,

  // Settings
  filesSpec,
  billingSpec,
  accountSettingsSpec,
  // helpCenterSpec and supportTicketsSpec are intentionally excluded from
  // the group nav — they're rendered in the pinned sidebar footer instead.
];

/**
 * Initialize the module registry with all specs.
 * Call this once at application startup.
 */
export function initializeModuleRegistry(): void {
  registerModules(allModuleSpecs);
}

// Re-export individual specs for direct access if needed
export {
  homeSpec,
  workflowsSpec,
  chatbotsSpec,
  templatesSpec,
  channelsSpec,
  integrationsSpec,
  emailDomainsSpec,
  campaignsSpec,
  conversationsSpec,
  calendarSpec,
  cartSpec,
  ticketsSpec,
  analyticsSpec,
  socialListeningSpec,
  logsSpec,
  customerPeopleSpec,
  customerSegmentsSpec,
  customerEventsSpec,
  customerLabelsSpec,
  customerDashboardSpec,
  customerRulesSpec,
  customerGoalsSpec,
  customerPropertiesSpec,
  customerPersonasSpec,
  customerListsSpec,
  helpCenterSpec,
  supportTicketsSpec,
  usersSpec,
  filesSpec,
  billingSpec,
  accountSettingsSpec,
};
